/**
 * <ax-table-of-contents> — Sommaire automatique d'article de blog.
 *
 * Lit les H2/H3 du contenu de l'article (rendu dans la section `main-article`),
 * leur injecte un `id` slugifié si absent, et construit la liste de liens du sommaire.
 * Chaque H2 ayant des H3 devient un <details> repliable, un seul ouvert à la fois.
 * Le contenu et le sommaire vivant dans deux sections distinctes, la seule donnée
 * partagée est le DOM final : ce composant fait le pont côté client.
 *
 * Le positionnement en rail (colonne gauche) est géré par `blog-post-layout.js`.
 * Ce composant se contente de remplir la liste, gérer l'état actif au scroll, et
 * masquer sa section si moins de `min-headings` titres sont trouvés.
 *
 * Attributs :
 *   - content-selector : sélecteur du conteneur de contenu (def. ".article-main .prose")
 *   - min-headings     : nb mini de titres pour afficher le sommaire (def. 2)
 */
class AxTableOfContents extends HTMLElement {
  connectedCallback() {
    this.contentSelector = this.getAttribute('content-selector') || '.article-main .prose';
    this.minHeadings = parseInt(this.getAttribute('min-headings'), 10) || 2;
    this.list = this.querySelector('[data-toc-list]');
    this.arrowTemplate = this.querySelector('[data-toc-arrow]');
    this.section = this.closest('.shopify-section') || this;
    this.accordionName = `ax-toc-${this.section.id || 'default'}`;

    // `blog-post-layout.js` déplace ce nœud (rail ⇄ pile au resize) : chaque
    // reconnexion rejoue `connectedCallback`, d'où le retrait groupé au disconnect.
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.list.addEventListener('click', (event) => this.onListClick(event), { signal });
    // `toggle` ne bulle pas : délégation en phase de capture.
    this.list.addEventListener('toggle', (event) => this.onToggle(event), {
      capture: true,
      signal,
    });

    this.build();

    // Le theme editor re-render les sections en AJAX : on rescanne sans dupliquer.
    document.addEventListener('shopify:section:load', () => this.build(), { signal });
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    if (this.observer) this.observer.disconnect();
  }

  build() {
    const content = document.querySelector(this.contentSelector);
    const headings = content
      ? Array.from(content.querySelectorAll('h2, h3')).filter(
          (h) => h.textContent.trim().length > 0
        )
      : [];

    if (headings.length < this.minHeadings) {
      this.setHidden(true);
      return;
    }

    this.used = new Set();
    this.list.innerHTML = '';
    this.groupHeadings(headings).forEach((group) => this.renderGroup(group));

    this.headings = headings;
    this.links = Array.from(this.list.querySelectorAll('.ax-toc__link'));
    this.setHidden(false);
    this.observeHeadings();
  }

  /** Rattache chaque H3 au H2 qui le précède (H3 orphelins : groupe sans titre). */
  groupHeadings(headings) {
    return headings.reduce((groups, heading) => {
      const isTop = heading.tagName === 'H2';
      const last = groups[groups.length - 1];

      if (isTop || !last) {
        groups.push({ heading: isTop ? heading : null, subs: isTop ? [] : [heading] });
      } else {
        last.subs.push(heading);
      }

      return groups;
    }, []);
  }

  renderGroup(group) {
    if (!group.heading) {
      group.subs.forEach((sub) => this.list.appendChild(this.createItem(sub, true)));
      return;
    }

    if (!group.subs.length) {
      this.list.appendChild(this.createItem(group.heading, false));
      return;
    }

    const item = document.createElement('li');
    item.className = 'ax-toc__item';

    const details = document.createElement('details');
    details.className = 'ax-toc__details';
    // Accordéon exclusif natif ; `onToggle` prend le relais si `name` est ignoré.
    details.setAttribute('name', this.accordionName);

    const summary = document.createElement('summary');
    summary.className = 'ax-toc__summary';
    summary.appendChild(this.createLink(group.heading));
    if (this.arrowTemplate) {
      summary.appendChild(this.arrowTemplate.content.cloneNode(true));
    }

    const sublist = document.createElement('ul');
    sublist.className = 'ax-toc__sublist';
    group.subs.forEach((sub) => sublist.appendChild(this.createItem(sub, true)));

    details.append(summary, sublist);
    item.appendChild(details);
    this.list.appendChild(item);
  }

  createItem(heading, isSub) {
    const item = document.createElement('li');
    item.className = 'ax-toc__item' + (isSub ? ' ax-toc__item--sub' : '');
    item.appendChild(this.createLink(heading));
    return item;
  }

  createLink(heading) {
    if (!heading.id || this.used.has(heading.id)) {
      heading.id = this.uniqueSlug(heading.textContent, this.used);
    }
    this.used.add(heading.id);

    const link = document.createElement('a');
    link.className = 'ax-toc__link';
    link.href = `#${heading.id}`;
    link.textContent = heading.textContent.trim();
    return link;
  }

  /** Un lien dans un <summary> replierait le groupe : on navigue en le gardant ouvert. */
  onListClick(event) {
    const link = event.target.closest?.('.ax-toc__summary > .ax-toc__link');
    if (!link) return;

    event.preventDefault();
    link.closest('.ax-toc__details').open = true;

    const href = link.getAttribute('href');
    if (window.location.hash === href) {
      document.getElementById(href.slice(1))?.scrollIntoView();
    } else {
      window.location.hash = href;
    }
  }

  onToggle(event) {
    const details = event.target;
    if (!details.open || !details.classList.contains('ax-toc__details')) return;

    this.list.querySelectorAll('.ax-toc__details[open]').forEach((other) => {
      if (other !== details) other.open = false;
    });
  }

  /** Slug propre : sans accents, ponctuation, doublons dédupliqués (conclusion-2…). */
  uniqueSlug(text, used) {
    const base =
      text
        .trim()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'section';

    let slug = base;
    let i = 2;
    while (used.has(slug) || document.getElementById(slug)) {
      slug = `${base}-${i++}`;
    }
    return slug;
  }

  /** Surligne l'entrée du titre actuellement visible et déplie son groupe. */
  observeHeadings() {
    if (this.observer) this.observer.disconnect();
    if (!('IntersectionObserver' in window) || !this.headings.length) return;

    const byId = new Map(this.links.map((l) => [l.getAttribute('href').slice(1), l]));

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const link = byId.get(entry.target.id);
          if (!link) return;
          this.links.forEach((l) => l.classList.remove('is-active'));
          link.classList.add('is-active');

          const group = link.closest('.ax-toc__details');
          if (group) group.open = true;
        });
      },
      { rootMargin: '0px 0px -75% 0px', threshold: 0 }
    );

    this.headings.forEach((h) => this.observer.observe(h));
  }

  /** Masque/affiche la section rail et notifie l'orchestrateur de recalculer. */
  setHidden(hidden) {
    if (this.section.hidden === hidden) return;
    this.section.hidden = hidden;
    document.dispatchEvent(new CustomEvent('blog-post-rail:change'));
  }
}

if (!customElements.get('ax-table-of-contents')) {
  customElements.define('ax-table-of-contents', AxTableOfContents);
}
